import {
  type ActiveStaffItem,
  formatDuration,
  formatMoney,
  type ImportantFlag,
  LONG_OPEN_SHIFT_HOURS,
  PROVISIONING_PIPELINE,
  queryKeys,
  type StaffNotificationFeedItem,
  type SupportTicketListItem,
  type TaskListItem,
  type TimeEntryListItem,
  type UnitStatus,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  humanizeStatus,
  ORDER_STATUS_TONE,
  Skeleton,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import {
  AlertTriangle,
  Boxes,
  LifeBuoy,
  ListChecks,
  Monitor,
  PhoneMissed,
  ShoppingCart,
  Star,
} from 'lucide-react'
import { useEffect } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DashboardCustomizeMenu, useDashboardTiles } from '../components/dashboard-customize.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface OrderRow {
  id: string
  orderNumber: string
  status: string
  channel: string
  totalCents: number
  customerName: string | null
  createdAt: string
}

interface KioskRow {
  id: string
  name: string
  online: boolean
  status: string
}

interface StockRow {
  productId: string
  productName: string
  qty: number
  available: number
  reorderPoint: number | null
  effectiveReorderPoint: number
}

interface StaffRow {
  id: string
  name: string
  role: string
  isActive: boolean
  lastLoginAt: string | null
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`
  return `${Math.floor(seconds / 86_400)} days ago`
}

/** How many rows the list cards (notifications, staff) show before "View all". */
const LIST_CARD_ROWS = 6

/** Today as YYYY-MM-DD in the browser's timezone, to compare against task due dates. */
function todayKey(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** One line of the dashboard's "To do" list: a task or ticket waiting on me (t1335). */
interface TodoRow {
  key: string
  to: string
  title: string
  detail: string
  overdue: boolean
}

function buildTodoRows(tasks: TaskListItem[], tickets: SupportTicketListItem[]): TodoRow[] {
  const today = todayKey()
  const taskRows = [...tasks]
    .sort((a, b) => {
      if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate)
      if (a.dueDate) return -1
      if (b.dueDate) return 1
      return 0
    })
    .map((t): TodoRow => {
      const overdue = !!t.dueDate && t.dueDate < today
      const due = !t.dueDate
        ? ''
        : overdue
          ? ' · Overdue'
          : t.dueDate === today
            ? ' · Due today'
            : ` · Due ${t.dueDate}`
      return {
        key: `task-${t.id}`,
        to: `/tasks/${t.id}`,
        title: t.title,
        detail: `Task ${t.taskNumber}${due}`,
        overdue,
      }
    })
  const ticketRows = tickets.map(
    (t): TodoRow => ({
      key: `ticket-${t.id}`,
      to: `/support-tickets/${t.id}`,
      title: t.subject,
      detail: `Ticket ${t.ticketNumber} · ${t.customerName ?? t.contactPhone ?? t.contactEmail ?? 'open'}`,
      overdue: false,
    }),
  )
  return [...taskRows, ...ticketRows]
}

function StatTile({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
  isLoading,
  to,
}: {
  label: string
  value: string | number
  icon: LucideIcon
  tone?: 'neutral' | 'warning' | 'danger'
  isLoading?: boolean
  to?: string
}) {
  const content = (
    <Card className="transition-colors hover:border-border-strong">
      <CardContent className="flex items-start justify-between gap-3 pt-4">
        <div className="min-w-0">
          <p className="text-xs text-foreground-muted">{label}</p>
          {isLoading ? (
            <Skeleton className="mt-1.5 h-7 w-16" />
          ) : (
            <p className="mt-0.5 text-2xl font-semibold tabular">{value}</p>
          )}
        </div>
        <div
          className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
            tone === 'danger'
              ? 'bg-danger-subtle text-danger'
              : tone === 'warning'
                ? 'bg-warning-subtle text-warning'
                : 'bg-surface-sunken text-foreground-muted'
          }`}
        >
          <Icon className="size-4" aria-hidden="true" />
        </div>
      </CardContent>
    </Card>
  )

  return to ? <Link to={to}>{content}</Link> : content
}

export function DashboardPage() {
  const { staff, can } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // A guest holds only wiki/workspace read-ish permissions (see
  // DEFAULT_ROLE_PERMISSIONS), so every widget below would be
  // permission-gated off, leaving an empty dashboard — send them straight to
  // whichever of the two they've actually been granted.
  useEffect(() => {
    if (staff?.role !== 'guest') return
    if (can('workspace:read')) void navigate({ to: '/workspace' })
    else if (can('wiki:read')) void navigate({ to: '/wiki' })
  }, [staff, can, navigate])

  // A tile someone has hidden also stops asking the API for its data.
  const { isVisible } = useDashboardTiles()

  // Items colleagues marked important for me (t1377). Not a hideable tile: it
  // only shows when there is something in it.
  const { data: important } = useQuery({
    queryKey: queryKeys.important.mine(),
    queryFn: () => api.get<ImportantFlag[]>('/me/important'),
    refetchInterval: 60_000,
  })
  const clearImportant = useMutation({
    mutationFn: (id: string) => api.post(`/important/${id}/dismiss`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.important.all() }),
  })

  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: queryKeys.units.counts(),
    queryFn: () => api.get<Partial<Record<UnitStatus, number>>>('/units/counts'),
    enabled:
      isVisible('awaiting-provisioning') ||
      isVisible('ready-to-sell') ||
      isVisible('provisioning-pipeline'),
  })

  // Cancelled and refunded (fully or partly) orders stay off the card (t1279,
  // t1334); the Orders page can still show them.
  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: queryKeys.orders.list({
      status: 'except_cancelled',
      excludeRefunded: true,
      pageSize: 8,
    }),
    queryFn: () =>
      api.get<{ items: OrderRow[]; total: number }>('/orders', {
        query: { status: 'except_cancelled', excludeRefunded: true, pageSize: 8 },
      }),
    enabled: isVisible('recent-orders'),
  })

  // Not tied to the Kiosks offline tile: the warning banner under the cards
  // reads the same data and is not something that can be hidden.
  const { data: kiosks } = useQuery({
    queryKey: queryKeys.kiosks.list(),
    queryFn: () => api.get<KioskRow[]>('/kiosks'),
    enabled: can('kiosks:read'),
    refetchInterval: 60_000,
  })

  const { data: lowStock } = useQuery({
    queryKey: queryKeys.stock.levels({ lowOnly: true }),
    queryFn: () => api.get<StockRow[]>('/stock', { query: { lowOnly: true } }),
    enabled: isVisible('low-stock'),
  })

  // Counts only: page size 1, read `total`. Their own keys sit under the list
  // keys' prefix so ticket and task changes refresh them, without sharing a
  // cache entry with the (infinite) list queries on those pages.
  const { data: openTickets, isLoading: openTicketsLoading } = useQuery({
    queryKey: [...queryKeys.supportTickets.all(), 'open-count'] as const,
    queryFn: () =>
      api.get<{ total: number }>('/support-tickets', { query: { status: 'open', pageSize: 1 } }),
    enabled: isVisible('open-tickets'),
    refetchInterval: 60_000,
  })

  const { data: openTasks, isLoading: openTasksLoading } = useQuery({
    queryKey: [...queryKeys.tasks.all(), 'open-count'] as const,
    queryFn: () => api.get<{ total: number }>('/tasks', { query: { status: 'open', pageSize: 1 } }),
    enabled: isVisible('open-tasks'),
    refetchInterval: 60_000,
  })

  const { data: notifications, isLoading: notificationsLoading } = useQuery({
    queryKey: queryKeys.staffNotifications(),
    queryFn: () =>
      api.get<{ items: StaffNotificationFeedItem[] }>('/me/notifications', {
        query: { limit: LIST_CARD_ROWS },
      }),
    enabled: isVisible('recent-notifications'),
    refetchInterval: 60_000,
  })

  const canSeeTodo = isVisible('recent-notifications')
  const { data: myTasks, isLoading: myTasksLoading } = useQuery({
    queryKey: [...queryKeys.tasks.all(), 'mine-open'] as const,
    queryFn: () =>
      api.get<{ items: TaskListItem[]; total: number }>('/tasks', {
        query: { status: 'open', assignee: 'me', pageSize: 20 },
      }),
    enabled: canSeeTodo && can('tasks:read'),
    refetchInterval: 60_000,
  })

  const { data: myTickets, isLoading: myTicketsLoading } = useQuery({
    queryKey: [...queryKeys.supportTickets.all(), 'mine-open'] as const,
    queryFn: () =>
      api.get<{ items: SupportTicketListItem[]; total: number }>('/support-tickets', {
        query: { status: 'open', assignedStaffId: staff?.id, pageSize: 20 },
      }),
    enabled: canSeeTodo && can('support:read') && !!staff?.id,
    refetchInterval: 60_000,
  })

  const { data: staffList, isLoading: staffLoading } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
    enabled: isVisible('staff'),
  })

  const { data: onClock, isLoading: onClockLoading } = useQuery({
    queryKey: queryKeys.timeClock.list({ status: 'open', pageSize: 50 }),
    queryFn: () =>
      api.get<{ items: TimeEntryListItem[]; total: number }>('/time-clock/entries', {
        query: { status: 'open', pageSize: 50 },
      }),
    enabled: isVisible('on-the-clock'),
    refetchInterval: 60_000,
  })

  const { data: signedIn, isLoading: signedInLoading } = useQuery({
    queryKey: queryKeys.staff.active(),
    queryFn: () => api.get<ActiveStaffItem[]>('/staff/active'),
    enabled: isVisible('signed-in-now'),
    refetchInterval: 60_000,
  })

  // The API returns one row per location, so a product low at two locations
  // must not count twice — this matches the Stock page badge, which counts
  // products.
  const lowStockCount = new Set((lowStock ?? []).map((row) => row.productId)).size
  const awaitingProvisioning = (counts?.received ?? 0) + (counts?.provisioning ?? 0)
  const kiosksOffline =
    kiosks?.filter((k: KioskRow) => k.status === 'active' && !k.online).length ?? 0
  const totalPipeline = PROVISIONING_PIPELINE.reduce((sum, s) => sum + (counts?.[s] ?? 0), 0)
  const activeStaff = (staffList ?? []).filter((person) => person.isActive)
  const todoRows = buildTodoRows(myTasks?.items ?? [], myTickets?.items ?? [])
  const todoShown = todoRows.slice(0, 5)
  const todoLoading = myTasksLoading || myTicketsLoading

  const showLeftColumn = isVisible('recent-orders') || isVisible('recent-notifications')
  const showRightColumn =
    isVisible('provisioning-pipeline') ||
    isVisible('staff') ||
    isVisible('on-the-clock') ||
    isVisible('signed-in-now')

  return (
    <Page>
      <PageHeader
        title={`Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, ${staff?.name?.split(' ')[0] ?? ''}`}
        description="Here is where things stand right now."
        actions={<DashboardCustomizeMenu />}
      />

      {important && important.length > 0 ? (
        <Card className="mb-5 border-warning/40">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Star className="size-4 text-warning" aria-hidden="true" />
            <p className="text-sm font-medium">Important</p>
          </div>
          <ul>
            {important.map((flag) => (
              <li
                key={flag.id}
                className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-0"
              >
                <Link to={flag.url} className="min-w-0 flex-1 hover:underline">
                  <p className="truncate text-sm font-medium">{flag.label}</p>
                  <p className="truncate text-xs text-foreground-muted">
                    From {flag.createdByName ?? 'a colleague'} · {relativeTime(flag.createdAt)}
                    {flag.note ? ` · ${flag.note}` : ''}
                  </p>
                </Link>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={clearImportant.isPending}
                  onClick={() => clearImportant.mutate(flag.id)}
                >
                  Done
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {isVisible('awaiting-provisioning') ? (
          <StatTile
            label="Awaiting provisioning"
            value={awaitingProvisioning}
            icon={Boxes}
            tone={awaitingProvisioning > 20 ? 'warning' : 'neutral'}
            isLoading={countsLoading}
            to="/provisioning"
          />
        ) : null}
        {isVisible('ready-to-sell') ? (
          <StatTile
            label="Ready to sell"
            value={counts?.sellable ?? 0}
            icon={Boxes}
            isLoading={countsLoading}
            to="/provisioning"
          />
        ) : null}
        {isVisible('low-stock') ? (
          <StatTile
            label="Low stock items"
            value={lowStockCount}
            icon={AlertTriangle}
            tone={lowStockCount > 0 ? 'warning' : 'neutral'}
            to="/stock"
          />
        ) : null}
        {isVisible('kiosks-offline') ? (
          <StatTile
            label="Kiosks offline"
            value={kiosksOffline}
            icon={Monitor}
            tone={kiosksOffline > 0 ? 'danger' : 'neutral'}
            to="/machines"
          />
        ) : null}
        {isVisible('open-tickets') ? (
          <StatTile
            label="Open tickets"
            value={openTickets?.total ?? 0}
            icon={LifeBuoy}
            isLoading={openTicketsLoading}
            to="/support-tickets"
          />
        ) : null}
        {isVisible('open-tasks') ? (
          <StatTile
            label="Unfinished tasks"
            value={openTasks?.total ?? 0}
            icon={ListChecks}
            isLoading={openTasksLoading}
            to="/tasks"
          />
        ) : null}
      </div>

      {showLeftColumn || showRightColumn ? (
        <div
          className={`mt-5 grid gap-5 ${showLeftColumn && showRightColumn ? 'lg:grid-cols-[1fr_320px]' : ''}`}
        >
          {showLeftColumn ? (
            <div className="min-w-0 space-y-5">
              {isVisible('recent-orders') ? (
                <Card>
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">Recent orders</p>
                    <Link to="/orders" className="text-xs text-primary hover:underline">
                      View all
                    </Link>
                  </div>
                  <div>
                    {ordersLoading ? (
                      <div className="space-y-px p-4">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Skeleton key={i} className="h-8" />
                        ))}
                      </div>
                    ) : orders?.items.length === 0 ? (
                      <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                        No orders yet.
                      </p>
                    ) : (
                      <ul>
                        {orders?.items.map((order: OrderRow) => (
                          <li
                            key={order.id}
                            className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-0"
                          >
                            <span className="font-identifier text-sm">{order.orderNumber}</span>
                            <span className="truncate text-sm text-foreground-muted">
                              {order.customerName ?? 'Walk-in'}
                            </span>
                            <Badge
                              tone={ORDER_STATUS_TONE[order.status] ?? 'neutral'}
                              className="ms-auto"
                            >
                              {humanizeStatus(order.status)}
                            </Badge>
                            <span className="w-20 text-end text-sm tabular">
                              {formatMoney(order.totalCents)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>
              ) : null}

              {isVisible('recent-notifications') ? (
                <Card>
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">Recent notifications</p>
                    <Link to="/support-tickets" className="text-xs text-primary hover:underline">
                      View all
                    </Link>
                  </div>
                  {notificationsLoading || todoLoading ? (
                    <div className="space-y-px p-4">
                      {['a', 'b', 'c', 'd'].map((key) => (
                        <Skeleton key={key} className="h-8" />
                      ))}
                    </div>
                  ) : todoShown.length === 0 && notifications?.items.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                      Nothing waiting. Missed calls, your tasks and ticket alerts will show up here.
                    </p>
                  ) : (
                    <>
                      {todoShown.length > 0 ? (
                        <div>
                          <p className="border-b border-border bg-surface-sunken px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                            To do
                          </p>
                          <ul>
                            {todoShown.map((row) => (
                              <li key={row.key} className="border-b border-border">
                                <Link
                                  to={row.to}
                                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-sunken"
                                >
                                  <ListChecks
                                    className="size-4 shrink-0 text-foreground-subtle"
                                    aria-hidden
                                  />
                                  <div className="min-w-0">
                                    <p className="truncate text-sm">{row.title}</p>
                                    <p
                                      className={`truncate text-xs ${
                                        row.overdue ? 'text-danger' : 'text-foreground-muted'
                                      }`}
                                    >
                                      {row.detail}
                                    </p>
                                  </div>
                                </Link>
                              </li>
                            ))}
                            {todoRows.length > todoShown.length ? (
                              <li className="border-b border-border">
                                <Link
                                  to="/tasks"
                                  className="block px-4 py-2 text-xs text-primary hover:underline"
                                >
                                  +{todoRows.length - todoShown.length} more waiting on you
                                </Link>
                              </li>
                            ) : null}
                          </ul>
                        </div>
                      ) : null}
                      {notifications && notifications.items.length > 0 ? (
                        <div>
                          {todoShown.length > 0 ? (
                            <p className="border-b border-border bg-surface-sunken px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                              Recent
                            </p>
                          ) : null}
                          <ul>
                            {notifications.items.map((item) => (
                              <li key={item.id} className="border-b border-border last:border-0">
                                <Link
                                  to={item.url ?? '/support-tickets'}
                                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-sunken"
                                >
                                  {item.type === 'missed-call' ? (
                                    <PhoneMissed
                                      className="size-4 shrink-0 text-danger"
                                      aria-hidden
                                    />
                                  ) : null}
                                  {item.type === 'flagged-important' ? (
                                    <Star
                                      className="size-4 shrink-0 text-warning"
                                      aria-hidden
                                    />
                                  ) : null}
                                  <div className="min-w-0">
                                    <p className="truncate text-sm">{item.title}</p>
                                    {item.body ? (
                                      <p className="truncate text-xs text-foreground-muted">
                                        {item.body}
                                      </p>
                                    ) : null}
                                  </div>
                                  <span className="ms-auto shrink-0 text-xs text-foreground-subtle">
                                    {relativeTime(item.createdAt)}
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </>
                  )}
                </Card>
              ) : null}
            </div>
          ) : null}

          {showRightColumn ? (
            <div className="min-w-0 space-y-5">
              {isVisible('provisioning-pipeline') ? (
                <Card>
                  <div className="border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">Provisioning pipeline</p>
                  </div>
                  <CardContent className="space-y-2.5 pt-4">
                    {PROVISIONING_PIPELINE.map((stage) => {
                      const count = counts?.[stage] ?? 0
                      const share = totalPipeline > 0 ? (count / totalPipeline) * 100 : 0
                      return (
                        <div key={stage}>
                          <div className="flex items-baseline justify-between text-xs">
                            <span className="text-foreground-muted">{humanizeStatus(stage)}</span>
                            <span className="font-medium tabular">{count}</span>
                          </div>
                          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              ) : null}

              {isVisible('staff') ? (
                <Card>
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">Staff</p>
                    <Link to="/staff" className="text-xs text-primary hover:underline">
                      View all
                    </Link>
                  </div>
                  {staffLoading ? (
                    <div className="space-y-px p-4">
                      {['a', 'b', 'c', 'd'].map((key) => (
                        <Skeleton key={key} className="h-8" />
                      ))}
                    </div>
                  ) : activeStaff.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                      No active staff.
                    </p>
                  ) : (
                    <>
                      <ul>
                        {activeStaff.slice(0, LIST_CARD_ROWS).map((person) => (
                          <li
                            key={person.id}
                            className="flex items-center gap-2 border-b border-border px-4 py-2.5 last:border-0"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm">{person.name}</p>
                              <p className="text-xs text-foreground-subtle">
                                Last signed in {relativeTime(person.lastLoginAt)}
                              </p>
                            </div>
                            <Badge tone="neutral" className="ms-auto shrink-0">
                              {humanizeStatus(person.role)}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                      <p className="border-t border-border px-4 py-2 text-xs text-foreground-muted">
                        {activeStaff.length} active
                      </p>
                    </>
                  )}
                </Card>
              ) : null}

              {isVisible('on-the-clock') ? (
                <Card>
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">On the clock</p>
                    <Link to="/time-clock" className="text-xs text-primary hover:underline">
                      View all
                    </Link>
                  </div>
                  {onClockLoading ? (
                    <div className="space-y-px p-4">
                      {['a', 'b', 'c'].map((key) => (
                        <Skeleton key={key} className="h-8" />
                      ))}
                    </div>
                  ) : (onClock?.items.length ?? 0) === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                      Nobody is clocked in.
                    </p>
                  ) : (
                    <>
                      <ul>
                        {onClock?.items.slice(0, LIST_CARD_ROWS).map((entry) => {
                          const openMs = Date.now() - new Date(entry.clockInAt).getTime()
                          const stale = openMs > LONG_OPEN_SHIFT_HOURS * 3_600_000
                          return (
                            <li
                              key={entry.id}
                              className="flex items-center gap-2 border-b border-border px-4 py-2.5 last:border-0"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm">{entry.userName}</p>
                                <p className="text-xs text-foreground-subtle">
                                  Since{' '}
                                  {new Date(entry.clockInAt).toLocaleTimeString([], {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}{' '}
                                  · {formatDuration(openMs)}
                                </p>
                              </div>
                              {stale ? (
                                <Badge
                                  tone="warning"
                                  className="ms-auto shrink-0"
                                  title="Probably a forgotten clock-out"
                                >
                                  Open · {formatDuration(openMs)}
                                </Badge>
                              ) : (
                                <Badge tone="success" className="ms-auto shrink-0">
                                  On the clock
                                </Badge>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                      <p className="border-t border-border px-4 py-2 text-xs text-foreground-muted">
                        {onClock?.total ?? 0} clocked in
                      </p>
                    </>
                  )}
                </Card>
              ) : null}

              {isVisible('signed-in-now') ? (
                <Card>
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">Signed in now</p>
                    <Link to="/staff" className="text-xs text-primary hover:underline">
                      View all
                    </Link>
                  </div>
                  {signedInLoading ? (
                    <div className="space-y-px p-4">
                      {['a', 'b', 'c'].map((key) => (
                        <Skeleton key={key} className="h-8" />
                      ))}
                    </div>
                  ) : (signedIn?.length ?? 0) === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                      Nobody has been active in the last 10 minutes.
                    </p>
                  ) : (
                    <>
                      <ul>
                        {signedIn?.slice(0, LIST_CARD_ROWS).map((person) => (
                          <li
                            key={person.id}
                            className="flex items-center gap-2 border-b border-border px-4 py-2.5 last:border-0"
                          >
                            <span
                              className="size-2 shrink-0 rounded-full bg-success"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p className="truncate text-sm">{person.name}</p>
                              <p className="text-xs text-foreground-subtle">
                                Active {relativeTime(person.lastSeenAt)}
                              </p>
                            </div>
                            <Badge tone="neutral" className="ms-auto shrink-0">
                              {humanizeStatus(person.role)}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                      <p className="border-t border-border px-4 py-2 text-xs text-foreground-muted">
                        {signedIn?.length ?? 0} signed in
                      </p>
                    </>
                  )}
                </Card>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {kiosksOffline > 0 ? (
        <Card className="mt-5 border-warning/40">
          <CardContent className="flex items-center gap-3 pt-4">
            <AlertTriangle className="size-4 shrink-0 text-warning" aria-hidden="true" />
            <p className="text-sm">
              {kiosksOffline} kiosk{kiosksOffline === 1 ? '' : 's'} not reporting in.{' '}
              <Link to="/machines" className="text-primary hover:underline">
                Check the fleet
              </Link>
            </p>
          </CardContent>
        </Card>
      ) : null}
    </Page>
  )
}

export { ShoppingCart }
